/**
 * Session-scoped request deduplication cache for booked-slot/details lookups.
 *
 * Why: Multiple components (TherapyReportPreview, EMRNextVisitComponent) independently
 * fetch the same endpoint for the same eventId. Without a shared cache every mount — or
 * every sibling component — fires a redundant HTTP round-trip.
 *
 * How it works:
 *  - The cache stores Promises (not resolved values) keyed by eventId.
 *  - The first caller for a given eventId creates the Promise and stores it.
 *  - Every subsequent caller for the same eventId gets back the *same* Promise, so the
 *    network request is issued exactly once regardless of how many components ask for it.
 *  - This works even when two components ask simultaneously (before either has resolved),
 *    because they share the same in-flight Promise.
 *  - The cache lives for the lifetime of the JS module (page session). A page reload clears it.
 */
import { getDataWithoutParams, postDataWithoutParams } from 'src/consumer/services/featureServices'
import { isCommonResponseSuccessful, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'

/** @type {Map<string, Promise<object|null>>} */
const cache = new Map()
/** @type {Map<string, Array<(value: object|null) => void>>} */
const pendingResolversByEventId = new Map()
const pendingEventIds = new Set()
let isBatchFlushScheduled = false
const RELATED_BOOKED_EVENTS_BRIEF_API = '/api/secure/practitioner/clinical/related-booked-events/brief'

function getEventIdFromItem(item) {
  return item?.eventId || item?.bookedEventId || item?.id || item?.bookedEvent?.id || null
}

function normalizeBriefDataToMap(data) {
  const map = new Map()
  if (!data) return map

  const mapCandidates = [
    data?.eventsById,
    data?.bookedEventsById,
    data?.relatedBookedEventsById,
  ]

  mapCandidates.forEach((candidate) => {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      Object.entries(candidate).forEach(([eventId, item]) => {
        if (eventId && item) map.set(eventId, item)
      })
    }
  })

  const listCandidates = [
    data?.events,
    data?.bookedEvents,
    data?.relatedBookedEvents,
    data?.eventBriefs,
    data?.bookedEventBriefs,
    data?.briefs,
  ]
  listCandidates.forEach((list) => {
    if (Array.isArray(list)) {
      list.forEach((item) => {
        const eventId = getEventIdFromItem(item)
        if (eventId) map.set(eventId, item)
      })
    }
  })

  return map
}

async function fetchBookedSlotBriefBatch(eventIds) {
  const uniqueIds = [...new Set((eventIds || []).filter(Boolean))]
  if (!uniqueIds.length) return new Map()

  try {
    const response = await postDataWithoutParams(RELATED_BOOKED_EVENTS_BRIEF_API, {
      eventIds: uniqueIds,
    })
    const responseData = response?.data
    const data =
      responseData?.data?.eventsById ||
      responseData?.data?.relatedBookedEventsById ||
      responseData?.data?.bookedEventsById
        ? responseData?.data
        : (responseData?.data ?? responseData)
    if (
      response?.status === 200 &&
      (isCommonResponseSuccessful(responseData?.code) || isCommonResponseSuccessfulV2(response))
    ) {
      return normalizeBriefDataToMap(data)
    }
  } catch (error) {}

  // Fallback only for single-id fetches if batch API fails unexpectedly.
  if (uniqueIds.length === 1) {
    const eventId = uniqueIds[0]
    try {
      const response = await getDataWithoutParams(
        `/api/secure/user/booked-slot/details?eventId=${eventId}&send-auxiliary-data=false`,
      )
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        response?.data?.data?.bookedEvent
      ) {
        return new Map([[eventId, response.data.data.bookedEvent]])
      }
    } catch (error) {}
  }

  return new Map()
}

/**
 * Fetch and cache booked-slot details for a given eventId.
 * Returns a Promise that resolves to the bookedEvent object, or null on failure.
 *
 * @param {string} eventId
 * @returns {Promise<object|null>}
 */
export function getBookedSlotDetails(eventId) {
  if (!eventId) return Promise.resolve(null)

  if (cache.has(eventId)) {
    return cache.get(eventId)
  }

  const deferredPromise = new Promise((resolve) => {
    const resolvers = pendingResolversByEventId.get(eventId) || []
    resolvers.push(resolve)
    pendingResolversByEventId.set(eventId, resolvers)
  })
  cache.set(eventId, deferredPromise)
  pendingEventIds.add(eventId)

  if (!isBatchFlushScheduled) {
    isBatchFlushScheduled = true
    queueMicrotask(async () => {
      isBatchFlushScheduled = false
      const idsToFlush = [...pendingEventIds]
      pendingEventIds.clear()
      if (!idsToFlush.length) return

      const itemsByEventId = await fetchBookedSlotBriefBatch(idsToFlush)
      idsToFlush.forEach((id) => {
        const resolvedItem = itemsByEventId.get(id) || null
        const resolvers = pendingResolversByEventId.get(id) || []
        pendingResolversByEventId.delete(id)
        // Replace cache with resolved promise for stable cache-hit path.
        cache.set(id, Promise.resolve(resolvedItem))
        resolvers.forEach((resolver) => resolver(resolvedItem))
      })
    })
  }

  return deferredPromise
}

/**
 * Pre-warm the cache for a list of eventIds in parallel.
 * Call this as early as possible (e.g. when the parent component mounts with a known
 * list of reports) so that by the time child components ask for their individual entries
 * the data is already in-flight or resolved.
 *
 * Duplicate ids in the array are silently deduplicated.
 *
 * @param {string[]} eventIds
 */
export function prefetchBookedSlotDetails(eventIds) {
  const unique = [...new Set(eventIds.filter(Boolean))]
  unique.forEach((id) => {
    getBookedSlotDetails(id)
  })
}

/**
 * Invalidate a single entry (e.g. after the slot is modified).
 * @param {string} eventId
 */
export function invalidateBookedSlotDetails(eventId) {
  cache.delete(eventId)
}

/** Clear the entire cache (useful in tests or on logout). */
export function clearBookedSlotCache() {
  cache.clear()
}
